import { fetcher } from "@api/_base";
import {
  AttributesResponse,
  CategoryHeaderResponse,
  CategoryMappingResponse,
} from "@interfaces/dto/categories.dto";
import { composeQueryParams } from "@utils/query.utils";
import useSWR from "swr";
import { getFilterQuery } from "./_utils";

export const useCategoryRouteMapping = ({
  urlPath,
}: {
  urlPath: string | undefined;
}): CategoryMappingResponse => {
  const { data, error, mutate } = useSWR(
    urlPath ? `/nextapi/type?urlKey=${urlPath}` : undefined,
    fetcher
  );

  return {
    data: data && data.length > 0 ? data[0] : undefined,
    isLoading: !error && !data,
    error: error,
    mutate: mutate,
  };
};

export const useBrandRouteMapping = ({
  urlPath,
}: {
  urlPath: string | undefined;
}): CategoryMappingResponse => {
  const { data, error, mutate } = useSWR(
    urlPath ? `/nextapi/type?urlKey=brands/${urlPath}` : undefined,
    fetcher
  );

  return {
    data: data && data.length > 0 ? data[0] : undefined,
    isLoading: !error && !data,
    error: error,
    mutate: mutate,
  };
};

export const useCategoryHeader = (
  categoryId: string | undefined
): CategoryHeaderResponse => {
  const { data, error, mutate } = useSWR(
    categoryId ? `/nextapi/categories-header/${categoryId}` : undefined,
    fetcher
  );

  return {
    data: data && data.length > 0 ? data[0] : undefined,
    isLoading: !error && !data,
    error: error,
    mutate: mutate,
  };
};

export const useAttributes = ({
  categoryId,
  subcatIds,
  priceRange,
  brandIds,
}: {
  categoryId: string | undefined;
  subcatIds: number[] | undefined;
  priceRange: string | undefined;
  brandIds: number[] | undefined;
}): AttributesResponse => {
  let query: string | undefined = undefined;
  // require categoryId
  if (categoryId) {
    // [cat]
    const queryCat = getFilterQuery({
      field: "category_id",
      value: categoryId,
      layer: 3,
    });
    // [subcat]
    const querySubcat =
      subcatIds && subcatIds.length > 0
        ? `${getFilterQuery({
          field: "category_ids",
          value: subcatIds.join(","),
          layer: 0,
        })}`
        : undefined;
    // [price]
    const queryPrice = priceRange
      ? `${getFilterQuery({
        field: "price",
        value: priceRange,
        layer: 1,
      })}`
      : undefined;
    // [brand]
    const queryBrand =
      brandIds && brandIds.length > 0
        ? `${getFilterQuery({
          field: "brand",
          value: brandIds.join(","),
          layer: 2,
        })}`
        : undefined;

    query = composeQueryParams([queryCat, querySubcat, queryPrice, queryBrand]);
  }

  let { data, error, mutate } = useSWR(
    query ? `/nextapi/layernavigation?${query}` : undefined,
    fetcher
  );

  if (data) {
    // adjust values structure on price attribute (from object to array)
    data = {
      ...data,
      items: data.items.map((item: any) =>
        item.attr_code === "price"
          ? { ...item, values: [item.values] }
          : { ...item }
      ),
    };
  }

  return {
    data: data,
    isLoading: !error && !data,
    error: error,
    mutate: mutate,
  };
};
